import { AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader } from '@/shared/components/ui/card';
import { FeatureErrorBoundary } from '@/core/error-boundaries/public';
import { sentryReporter } from '@/core/observability/public';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import {
  KpiCard,
  BarChart,
  HorizontalBarChart,
  StackedBarChart,
  LineChart,
  AreaChart,
  PieChart,
  DonutChart,
  DataTable,
} from '@/shared/components/charts';
import { useWidgetData } from '../../hooks/useWidgetData';
import { useWidgetLabelResolver } from '../../hooks/useWidgetLabelResolver';
import { WidgetCardHeader } from './WidgetCardHeader';
import { WidgetCardSkeleton } from './WidgetCardSkeleton';
import type { WidgetDef, WidgetScope, WidgetDataDto } from '../../types';

interface WidgetCardProps {
  widget: WidgetDef;
  scope: WidgetScope;
  isEditing: boolean;
  isAdmin: boolean;
  onEdit: (widget: WidgetDef) => void;
  onDelete: (widgetId: string) => void;
}

function WidgetRenderer({
  widget,
  data,
  resolveLabel,
}: {
  widget: WidgetDef;
  data: WidgetDataDto;
  resolveLabel: (rawLabel: string) => string;
}) {
  switch (data.shape) {
    case 'scalar':
      return <KpiCard value={data.value} />;
    case 'series': {
      const points = (data.labels ?? []).map((label, i) => ({
        name: resolveLabel(label),
        value: data.values?.[i] ?? 0,
      }));
      if (widget.type === 'donut') return <DonutChart data={points} />;
      if (widget.type === 'pie') return <PieChart data={points} />;
      if (widget.type === 'horizontal_bar') return <HorizontalBarChart data={points} />;
      // The backend emits StackedBar widgets as a single-series `series` shape,
      // so they arrive here rather than as `series_multi`.
      if (widget.type === 'stacked_bar')
        return (
          <StackedBarChart
            data={{
              categories: (data.labels ?? []).map(resolveLabel),
              series: [{ name: widget.title, values: data.values ?? [] }],
            }}
          />
        );
      return <BarChart data={points} />;
    }
    case 'series_multi':
      // Reserved for a future multi-series backend shape; not emitted today.
      return (
        <StackedBarChart
          data={{
            categories: (data.labels ?? []).map(resolveLabel),
            series: (data.series ?? []).map((s) => ({ name: s.label, values: s.values })),
          }}
        />
      );
    case 'timeseries': {
      const tsData = {
        labels: data.labels ?? [],
        series: [{ name: widget.title, values: data.values ?? [] }],
      };
      if (widget.type === 'area') return <AreaChart data={tsData} />;
      return <LineChart data={tsData} />;
    }
    case 'rows': {
      const cols = (data.columns ?? []).map((c) => ({ key: c, label: c, type: 'string' as const }));
      const rows = (data.rows ?? []).map((row) =>
        Object.fromEntries((data.columns ?? []).map((c, i) => [c, (row as unknown[])[i]])),
      );
      return <DataTable columns={cols} rows={rows} title={widget.title} />;
    }
  }
}

function WidgetCardInner({ widget, scope, isEditing, isAdmin, onEdit, onDelete }: WidgetCardProps) {
  const { t } = useTranslation('dashboard');
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();
  const { data, isLoading, isError, refetch } = useWidgetData(widget.id, scope);
  const resolveLabel = useWidgetLabelResolver(widget, scope);

  const handleDelete = async () => {
    const { confirmed } = await confirm({
      title: t('widget.confirmDelete.title'),
      description: t('widget.confirmDelete.description'),
      variant: 'destructive',
    });
    if (confirmed) onDelete(widget.id);
  };

  return (
    <>
      <Card
        className="group flex h-full flex-col overflow-hidden"
        data-testid={`dashboard-widget-card-${widget.id}`}
      >
        <CardHeader className="shrink-0 pb-2 pt-3">
          <WidgetCardHeader
            title={widget.title}
            isEditing={isEditing}
            isAdmin={isAdmin}
            onEdit={() => onEdit(widget)}
            onDelete={handleDelete}
          />
        </CardHeader>

        <CardContent className="min-h-0 flex-1 pb-3">
          {isLoading && <WidgetCardSkeleton h={widget.layout.desktop.h} />}

          {isError && !isLoading && (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
              <AlertCircle className="h-5 w-5 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{t('error.loadFailed')}</p>
              <Button variant="ghost" size="sm" onClick={() => void refetch()}>
                {t('error.retry')}
              </Button>
            </div>
          )}

          {data && !isLoading && !isError && (
            <div className="h-full w-full">
              <WidgetRenderer widget={widget} data={data} resolveLabel={resolveLabel} />
            </div>
          )}
        </CardContent>
      </Card>

      {isOpen && options && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(v) => !v && handleCancel()}
          title={options.title}
          description={options.description}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          variant={options.variant}
        />
      )}
    </>
  );
}

export function WidgetCard(props: WidgetCardProps) {
  return (
    <FeatureErrorBoundary reporter={sentryReporter}>
      <WidgetCardInner {...props} />
    </FeatureErrorBoundary>
  );
}
